import React, { useEffect, useState } from "react";
import "./PackagingSpendGraph.scss";
import Filter from "../Filter/Filter";
import { FilterOptionSchema } from "../Filter/filter.data";
import { Chart } from "chart.js";
import ApiService from "../../services/apiService";
import URLS from "../../urls/Urls";

interface IPackagingSpendGraphProps {
  setTotalSpentThisMonth: (expense: number) => void;
}

const PackagingSpendGraph: React.FC<IPackagingSpendGraphProps> = ({
  setTotalSpentThisMonth,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [addresses, setAddersses] = useState<FilterOptionSchema[]>([]);

  const [selectedAddress, setSelectedAddress] = useState<FilterOptionSchema[]>(
    []
  );

  useEffect(() => {
    if (addresses.length === 0) {
      getAddressWiseData();
    }
  }, []);

  const getAddressWiseData = async () => {
    try {
      setLoading(true);

      const { data } = await ApiService().client.get(
        URLS.DASHBOARD.ADDRESS_EXPENDITURE
      );

      setTotalSpentThisMonth(data.total_spent_this_month);
      setAddersses(data.addresses);

      if (data.addresses.length > 0) {
        const defaultAddress = data.addresses[0];
        setSelectedAddress([defaultAddress]);

        //  canvas and DOM settle before rendering
        setTimeout(() => {
          packagingConsumptionRender(defaultAddress);
        }, 0);
      }
    } catch (error) {
      console.error("Failed Getting Information On Address Expenditure", error);
    } finally {
      setLoading(false);
    }
  };

  const clearPackagingExpenditureChart = async () => {
    const chartStatus = Chart.getChart("packaging-expenditure-graph"); // <canvas> id
    if (chartStatus != undefined) {
      chartStatus.destroy();
    }
  };

  const getRolling12Months = (): string[] => {
    const baseMonths = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sept",
      "Oct",
      "Nov",
      "Dec",
    ];
    const currentMonthIndex = new Date().getMonth(); // 0-indexed
    const months = [];

    for (let i = 0; i < 12; i++) {
      const monthIndex = (currentMonthIndex - 11 + i + 12) % 12;
      months.push(baseMonths[monthIndex]);
    }

    return months;
  };

  const packagingConsumptionRender = async (address: FilterOptionSchema) => {
    await clearPackagingExpenditureChart();

    const canvas = document.getElementById("packaging-expenditure-graph");
    if (canvas) {
      canvas.setAttribute("height", `${10 * 16 + 24}px`);

      const maxValue = Math.max(...(address.values || [0]));
      const roundedMax = Math.ceil(maxValue / 1000) * 1000;
      const stepSize = Math.ceil(roundedMax / 4);

      new Chart(canvas as HTMLCanvasElement, {
        type: "line",
        data: {
          labels: getRolling12Months(),
          datasets: [
            {
              label: address.name || "Unknown",
              data: address.values || [],
              borderColor: "#5151EC",
              borderWidth: 2,
              pointStyle: "circle",
              pointRadius: 4,
              backgroundColor: "#FFFFFF",
            },
          ],
        },
        options: {
          elements: {
            line: {
              tension: 0.5,
            },
          },
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize,
                count: 5,
                color: "#33333399",
                padding: 12,
                font: {
                  size: 12,
                  weight: 500,
                  family: "Inter",
                },
                callback: function (tickValue: string | number) {
                  if (typeof tickValue === "number") {
                    return tickValue.toLocaleString("en-IN");
                  }
                  return tickValue;
                },
              },
              grid: {
                display: true,
                color: "#E5E7EB",
              },
              border: {
                display: false,
              },
            },
            x: {
              offset: true,
              grid: {
                display: false,
              },
              ticks: {
                color: "#333333",
                font: {
                  size: 12,
                  lineHeight: 1.5,
                  weight: 500,
                  family: "Inter",
                },
              },
            },
          },
        },
      });
    }
  };

  return (
    <div className="packaging-monthly-main-chart-container py-14 px-16 flex-column gap-18 flex-1">
      <div className="flex-row align-items-center justify-content-space-between">
        <p style={{ color: "#333333CC" }} className="xetgo-font-tag bold">
          Packaging Spend by Month
        </p>
        <div className="reports-address-container">
          <Filter
            option={{
              key: "address",
              name: "Address",
            }}
            optionList={addresses}
            multipleSelection={false}
            onSelection={(val, _option) => {
              if (val && val.length > 0) {
                setSelectedAddress(val);
                const selected = val[0];

                if (selected.values && selected.values.length > 0) {
                  packagingConsumptionRender(selected);
                }
              } else {
                setSelectedAddress([]);
              }
            }}
            preSelected={selectedAddress}
            mode="showNames"
            showPlaceholder={false}
            addNew={false}
            showSearch={true}
            classname="report-address-filter"
          />
        </div>
      </div>
      {loading ? (
        <div className="empty-state-report-address-wise flex-row align-items-center flex-1 justify-content-center">
          <div className="mini-loader"></div>
        </div>
      ) : (
        <div className="">
          <canvas id="packaging-expenditure-graph"></canvas>
        </div>
      )}
    </div>
  );
};

export default PackagingSpendGraph;
